
import { useState, useEffect } from 'react';
import { useSupabaseClient } from './useSupabaseClient';
import { useToast } from './use-toast';
import { Procurement } from '@/types/procurement';

type DashboardStats = {
  activeProcurements: number;
  completedAnalyses: number;
  savedDocuments: number;
};

type ChartDataPoint = {
  name: string;
  value: number;
};

export const useDashboard = () => {
  const supabase = useSupabaseClient();
  const { toast } = useToast();
  const [userName, setUserName] = useState('');
  const [stats, setStats] = useState<DashboardStats>({
    activeProcurements: 0,
    completedAnalyses: 0,
    savedDocuments: 0
  });
  const [recentProcurements, setRecentProcurements] = useState<Procurement[]>([]);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([
    { name: 'Jan', value: 0 },
    { name: 'Feb', value: 0 },
    { name: 'Mar', value: 0 },
    { name: 'Apr', value: 0 },
    { name: 'Maj', value: 0 },
    { name: 'Jun', value: 0 },
  ]);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user?.user_metadata?.name) {
        setUserName(user.user_metadata.name);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Get count of active procurements
      const { count: activeProcurements, error: errorActive } = await supabase
        .from('procurements')
        .select('*', { count: 'exact', head: true });
        
      if (errorActive) throw errorActive;
      
      // Get count of completed analyses
      const { count: completedAnalyses, error: errorCompleted } = await supabase
        .from('analyses')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'Klar');
        
      if (errorCompleted) throw errorCompleted;
      
      // Get the 3 most recent procurements
      const { data: recentProcs, error: errorRecent } = await supabase
        .from('procurements')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(3);
        
      if (errorRecent) throw errorRecent;
      
      // Get analyses by month (for chart)
      const { data: analysesByMonth, error: errorChart } = await supabase
        .from('analyses')
        .select('created_at')
        .eq('status', 'Klar');
        
      if (errorChart) throw errorChart;
      
      // Process chart data
      const monthCounts = [0, 0, 0, 0, 0, 0];
      if (analysesByMonth) {
        analysesByMonth.forEach(analysis => {
          const month = new Date(analysis.created_at).getMonth();
          // Only count last 6 months
          if (month >= 0 && month < 6) {
            monthCounts[month] += 1;
          }
        });
      }
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun'];
      const newChartData = months.map((name, index) => ({
        name,
        value: monthCounts[index]
      }));
      
      // Update state with fetched data
      setStats({
        activeProcurements: activeProcurements || 0,
        completedAnalyses: completedAnalyses || 0,
        savedDocuments: 15 // This is a placeholder - in a real app, it would be fetched
      });
      setRecentProcurements(recentProcs || []);
      setChartData(newChartData);
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte hämta dashboard-data: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
    fetchDashboardData();
  }, [supabase, toast]);

  return {
    userName,
    stats,
    recentProcurements,
    chartData,
    loading,
    refreshData: fetchDashboardData
  };
};
